import { HttpStatus } from '@maverick/http';
import {
    SourceApps,
    UserFave,
    UserRecentOrders,
    OloApiError,
    TransactionsHistory,
    TokenProvider,
    Rewards,
} from '@maverick/entity';
import { UserService } from '../services/olo/User.service';
import { LoyaltyService } from '../services/bbi/Loyalty.service';

export class UserManager {
    private readonly userService: UserService;
    private readonly loyaltyService: LoyaltyService;

    constructor(sourceApp: SourceApps) {
        this.userService = new UserService(sourceApp);
        this.loyaltyService = new LoyaltyService(sourceApp);
    }

    async retrieveUserRecentOrders(
        authtoken: string
    ): Promise<Partial<{ response: UserRecentOrders; error: OloApiError }>> {
        const response = await this.userService.retrieveUserRecentOrders({ authtoken });
        if (response.error) {
            if (response.status === HttpStatus.BadRequest) {
                const error = OloApiError.Make(response.body);
                return { error: error };
            }

            return {
                error: {
                    num: response.body.num ?? 0,
                    message: 'An error occurred while retrieving your recent orders. Please try again later.',
                },
            };
        }

        const recentOrders = UserRecentOrders.Make(response.body);

		const filteredRecentOrders = {
			...recentOrders,
			orders: recentOrders.orders.filter((order) => order.products && order.products.length > 0),
		};

		return { response: filteredRecentOrders };
	}

    async retrieveUserFaves(authtoken: string): Promise<Partial<{ response: UserFave; error: OloApiError }>> {
        const response = await this.userService.retrieveUserFaves({ authtoken });
        if (response.error) {
            if (response.status === HttpStatus.BadRequest) {
                const error = OloApiError.Make(response.body);
                return { error: error };
            }

            return {
                error: {
                    num: response.body.num ?? 0,
                    message: 'An error occurred while retrieving your faves. Please try again later.',
                },
            };
        }

		const favorites = UserFave.Make(response.body);

		const filteredFavorites = {
			...favorites,
			faves: favorites.faves.filter((fave) => 
				fave.products.some((product) => !product.disabled)
			),
		};

		filteredFavorites.faves = filteredFavorites.faves.reverse();
		return { response: filteredFavorites };
	}

    async getTransactionsHistoryByEmail(
        email: string
    ): Promise<Partial<{ transactionsHistory: TransactionsHistory; error: string }>> {
        const response = await this.loyaltyService.getTransactionsHistoryByEmail({ email });
        if (response.error) {
            return { error: response.errorMessage };
        }

        const rewards = TransactionsHistory.Make(response.body);
        return { transactionsHistory: rewards };
    }

    async authorizeUser(
        provider: string,
        providertoken: string,
        provideruserid: string,
        contactnumber: string | null,
        orderBasketid: string | null,
        cateringBasketid: string | null,
        firstname: string | null,
        lastname: string | null,
        emailaddress: string | null,
        newUser: string | null
    ): Promise<
        Partial<{
            orderTokenProvider: TokenProvider;
            cateringTokenProvider: TokenProvider;
            orderError: string;
            cateringError: string;
        }>
    > {
        const response = await this.userService.authorizeUser({
            provider,
            providertoken,
            provideruserid,
            contactnumber,
            orderBasketid,
            cateringBasketid,
            firstname,
            lastname,
            emailaddress,
        });
        if (response.responseOrder.error) {
            return { orderError: response.responseOrder.errorMessage };
        }

        if (response.responseCatering.error) {
            return { cateringError: response.responseCatering.errorMessage };
        }

        const orderTokenProvider = TokenProvider.Make({ ...response.responseOrder.body, newUser });
        const cateringTokenProvider = TokenProvider.Make({ ...response.responseCatering.body, newUser });

        return { orderTokenProvider: orderTokenProvider, cateringTokenProvider: cateringTokenProvider };
    }

    async getQualifyingRewards(
        authtoken: string,
        vendorId: number
    ): Promise<Partial<{ responseRewards: Array<Rewards>; error: string }>> {
        const response = await this.userService.getQualifyingRewards({ authtoken, vendorId });
        if (response.error) {
            return { error: response.errorMessage };
        }
        const rewards: Array<Rewards> = response.body.rewards.map((u: Rewards) => Rewards.Make(u));
        return { responseRewards: rewards };
    }
}
